import { RefObject, useCallback, useEffect, useState } from "react";

interface UseIsOverflowingReturn {
  isOverflowing: boolean;
  checkOverflow: () => boolean;
}

export const useIsOverflowing = <T extends HTMLElement>(
  ref: RefObject<T | null>
): UseIsOverflowingReturn => {
  const [isOverflowing, setIsOverflowing] = useState(false);

  const checkOverflow = useCallback(() => {
    if (!ref.current) return false;

    const element = ref.current;
    const { scrollWidth, clientWidth } = element;
    const shouldSetAsOverflowing = scrollWidth > clientWidth;

    setIsOverflowing(shouldSetAsOverflowing);

    return shouldSetAsOverflowing;
  }, [ref]);

  useEffect(() => {
    if (!ref.current) return undefined;

    checkOverflow();

    const handleResize = () => {
      checkOverflow();
    };
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [checkOverflow, ref]);

  useEffect(() => {
    if (!ref.current) return undefined;

    const resizeObserver = new ResizeObserver(() => {
      checkOverflow();
    });

    resizeObserver.observe(ref.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [checkOverflow, ref]);

  return { isOverflowing, checkOverflow };
};
